// src/ComparePlans.jsx

import { useAuth } from "./AuthContext";

const Check = () => (
  <span style={{ color: "var(--accent)", fontSize: "18px", lineHeight: "24px" }}>&#10003;</span>
);
const Dash = () => (
  <span style={{ color: "var(--border)", fontSize: "18px", lineHeight: "24px" }}>—</span>
);

const ComparePlans = ({ onBack, onUpgrade, isPaid, user }) => {
  const { signInWithGoogle } = useAuth();

  const features = [
    { name: "Chat with Irene", desc: "Ask questions about medical bills, insurance, and patient rights", free: true, pro: true },
    { name: "Local resource search", desc: "Irene searches for assistance programs in your area", free: true, pro: true },
    { name: "EOB & bill guidance", desc: "Understand what you're being charged and spot common errors", free: true, pro: true },
    { name: "Negotiation guidance", desc: "When to push back, what to say, and how to ask for a reduction or payment plan", free: true, pro: true },
    { name: "Cancer Cost Survival Guide", desc: "Our comprehensive guide to navigating cancer-related costs", free: true, pro: true },
    { name: "Conversation history", desc: "Pick up where you left off — your conversations are saved", free: false, pro: true },
    { name: "Bill & EOB upload", desc: "Upload your actual bills for AI-powered line-by-line analysis", free: false, pro: true },
    { name: "Error detection", desc: "On your uploaded bills, Irene flags possible billing errors, duplicate charges, and upcoding", free: false, pro: true },
  ];

  const handleCTA = () => {
    if (!user) {
      signInWithGoogle();
    } else if (!isPaid) {
      onUpgrade();
    }
  };

  return (
    <section style={{
      padding: "clamp(64px, 10vh, 120px) 24px 80px",
      maxWidth: "860px", margin: "0 auto",
    }}>
      <button
        onClick={onBack}
        aria-label="Go back"
        style={{
          background: "none", border: "none", cursor: "pointer",
          fontSize: "14px", color: "var(--ink-muted)", fontFamily: "inherit",
          marginBottom: "32px", padding: 0,
        }}
        onMouseEnter={(e) => e.target.style.color = "var(--ink)"}
        onMouseLeave={(e) => e.target.style.color = "var(--ink-muted)"}
      >
        &larr; Back
      </button>

      <h1 style={{
        fontFamily: "'DM Serif Display', serif",
        fontSize: "clamp(28px, 4vw, 40px)", fontWeight: "400",
        color: "var(--ink)", letterSpacing: "-0.02em",
        lineHeight: 1.2, marginBottom: "12px",
      }}>
        Simple, transparent pricing.
      </h1>
      <p style={{
        fontSize: "17px", color: "var(--ink-soft)", lineHeight: 1.7,
        marginBottom: "48px", maxWidth: "520px",
      }}>
        The chat advisor will always be free. Pro gives you the tools to take
        real action on your bills.
      </p>

      {/* Plan headers */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "1fr 120px 120px",
        gap: "0",
        alignItems: "end",
        marginBottom: "8px",
        padding: "0 0 16px",
        borderBottom: "2px solid var(--border)",
      }}>
        <div />
        <div style={{ textAlign: "center" }}>
          <p style={{
            fontSize: "13px", color: "var(--ink-muted)",
            textTransform: "uppercase", letterSpacing: "0.06em",
            marginBottom: "4px", fontWeight: "500",
          }}>Free</p>
          <p style={{
            fontSize: "28px", fontWeight: "600", color: "var(--ink)",
            fontFamily: "'DM Serif Display', serif",
          }}>$0</p>
        </div>
        <div style={{ textAlign: "center" }}>
          <p style={{
            fontSize: "13px", color: "var(--accent)",
            textTransform: "uppercase", letterSpacing: "0.06em",
            marginBottom: "4px", fontWeight: "600",
          }}>Pro</p>
          <p style={{
            fontSize: "28px", fontWeight: "600", color: "var(--ink)",
            fontFamily: "'DM Serif Display', serif",
          }}>$9<span style={{ fontSize: "14px", color: "var(--ink-muted)", fontFamily: "'Karla', sans-serif", fontWeight: "400" }}>/mo</span></p>
        </div>
      </div>

      {/* Feature rows */}
      {features.map((f, i) => (
        <div key={i} style={{
          display: "grid",
          gridTemplateColumns: "1fr 120px 120px",
          gap: "0",
          padding: "14px 0",
          borderBottom: "1px solid var(--border-light)",
          alignItems: "center",
        }}>
          <div>
            <p style={{
              fontSize: "15px", fontWeight: "500", color: "var(--ink)",
              marginBottom: "2px",
            }}>{f.name}</p>
            <p style={{
              fontSize: "13px", color: "var(--ink-muted)", lineHeight: 1.5,
            }}>{f.desc}</p>
          </div>
          <div style={{ textAlign: "center" }}>
            {f.free ? <Check /> : <Dash />}
          </div>
          <div style={{ textAlign: "center" }}>
            {f.pro ? <Check /> : <Dash />}
          </div>
        </div>
      ))}

      {/* CTA */}
      {!isPaid && (
        <div style={{
          textAlign: "center",
          paddingTop: "40px",
        }}>
          <button
            onClick={handleCTA}
            style={{
              padding: "16px 40px", fontSize: "16px", fontWeight: "500",
              backgroundColor: "var(--accent)", color: "#fff",
              border: "none", borderRadius: "8px", cursor: "pointer",
              fontFamily: "inherit", transition: "all 0.2s ease",
              letterSpacing: "0.01em",
            }}
            onMouseEnter={(e) => { e.target.style.backgroundColor = "var(--accent-light)"; e.target.style.transform = "translateY(-1px)"; }}
            onMouseLeave={(e) => { e.target.style.backgroundColor = "var(--accent)"; e.target.style.transform = "translateY(0)"; }}
          >
            {user ? "Upgrade to Pro" : "Sign in to upgrade"}
          </button>
          <p style={{
            fontSize: "13px", color: "var(--ink-muted)", marginTop: "12px",
          }}>
            Cancel anytime. No contracts.
          </p>
        </div>
      )}

      {isPaid && (
        <div style={{
          textAlign: "center", paddingTop: "40px",
        }}>
          <p style={{
            fontSize: "16px", color: "var(--accent)", fontWeight: "500",
          }}>
            You're on the Pro plan. Thank you for supporting Ask Irene.
          </p>
        </div>
      )}
    </section>
  );
};

export default ComparePlans;
